"use client";

import { useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import { useLocalizeHref } from "@marketing-sites/l10n/providers/localize-href-context";

const INTERNAL_CTA_ROUTES = ["/contact-sales", "/view-demos"];

const selectorsForRoute = (route: string) => [
  `a[href="${route}"]`,
  `a[href="${route}/"]`,
  `a[href^="${route}?"]`,
  `a[href^="${route}#"]`,
];

interface BuyerJourneyProps {
  journey: string;
  includeReferralPageDetail?: boolean;
}

export default function BuyerJourney({
  journey,
  includeReferralPageDetail = false,
}: BuyerJourneyProps) {
  const router = useRouter();
  const localizeHref = useLocalizeHref();

  const internalCtaSelector = useMemo(
    () =>
      Array.from(
        new Set(
          INTERNAL_CTA_ROUTES.flatMap((route) => [
            ...selectorsForRoute(route),
            ...selectorsForRoute(localizeHref(route)),
          ]),
        ),
      ).join(", "),
    [localizeHref],
  );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;
      const link = target.closest<HTMLAnchorElement>(internalCtaSelector);

      if (!link) return;

      try {
        const url = new URL(link.href);

        if (url.searchParams.has("journey")) return;

        url.searchParams.set("journey", journey);
        if (includeReferralPageDetail) {
          url.searchParams.set("referral_page_detail", window.location.href);
        }

        // Modifier clicks (Cmd+Click, Ctrl+Click, middle-click) and target="_blank"
        // should open in a new tab — mutate href and let the browser handle it.
        const isModifiedClick =
          e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

        if (isModifiedClick || link.target === "_blank") {
          link.href = url.toString();
          return;
        }

        // Use the raw attribute to detect internal links — jsdom resolves
        // relative hrefs against localhost, not window.location.origin.
        const rawHref = link.getAttribute("href") ?? "";
        const isInternal =
          rawHref.startsWith("/") || url.origin === window.location.origin;

        if (isInternal) {
          e.preventDefault();
          router.push(url.pathname + url.search);
        } else {
          link.href = url.toString();
        }
      } catch {
        return;
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [journey, router, internalCtaSelector]);

  return null;
}
